import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoyaltyBadge } from "@/components/app/LoyaltyBadge";
import { StatusBadge } from "@/components/app/StatusBadge";
import { Users, Plus, PencilSimple, Trash, CurrencyDollar, Lock, LockOpen, X } from "@phosphor-icons/react";
import { formatBRL, formatDate } from "@/lib/format";
import { toast } from "sonner";

/**
 * SubaccountsDialog — manage subaccounts of a primary client.
 * Props:
 *  - owner: the primary client object (id, name)
 *  - open / onOpenChange: dialog state
 *  - onChanged: callback when subaccounts list changes (so parent reloads)
 */
const emptySub = {
  name: "", username: "", phone: "", beneficiary: "",
  plan_value: "", recurrence: "mensal", due_day: 10, discount_pct: 0,
  custom_loyalty: "", notes: "", current_due_date: "",
};

export default function SubaccountsDialog({ owner, open, onOpenChange, onChanged }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | sub
  const [form, setForm] = useState(emptySub);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const load = async () => {
    if (!owner?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/clients/${owner.id}/subaccounts`);
      setSubs(data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404 || code === 405) {
        toast.error("Servidor desatualizado — atualize o backend para usar subcontas.");
      } else {
        toast.error(err?.response?.data?.detail || "Erro ao carregar subcontas");
      }
    }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) { load(); setEditing(null); } /* eslint-disable-next-line */ }, [open, owner?.id]);

  const startNew = () => {
    setEditing("new");
    setForm({ ...emptySub, due_day: 10 });
  };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", username: s.username || "", phone: s.phone || "",
      beneficiary: s.beneficiary || "",
      plan_value: s.plan_value || "", recurrence: s.recurrence || "mensal",
      due_day: s.due_day || 10, discount_pct: s.discount_pct || 0,
      custom_loyalty: s.custom_loyalty || "", notes: s.notes || "",
      current_due_date: s.current_due_date || "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    payload.plan_value = payload.plan_value === "" ? 0 : Number(payload.plan_value);
    payload.due_day = Number(payload.due_day);
    payload.discount_pct = Number(payload.discount_pct);
    if (!payload.custom_loyalty) delete payload.custom_loyalty;
    if (!payload.current_due_date) delete payload.current_due_date;
    if (!payload.beneficiary) delete payload.beneficiary;
    try {
      if (editing === "new") {
        await api.post(`/admin/clients/${owner.id}/subaccounts`, payload);
        toast.success("Subconta criada");
      } else {
        await api.put(`/admin/clients/${editing.id}`, payload);
        toast.success("Subconta atualizada");
      }
      setEditing(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir subconta ${s.name}? Esta ação remove todo o histórico.`)) return;
    try {
      await api.delete(`/admin/clients/${s.id}`);
      toast.success("Subconta removida");
      load(); onChanged?.();
    } catch { toast.error("Erro"); }
  };

  const setStatus = async (s, status) => {
    try {
      await api.put(`/admin/clients/${s.id}`, { status });
      toast.success("Status atualizado");
      load(); onChanged?.();
    } catch { toast.error("Erro"); }
  };

  const registerPay = async () => {
    if (!payTarget) return;
    const amount = Number(payAmount || payTarget.plan_value || 0);
    try {
      await api.post("/admin/payments", { user_id: payTarget.id, amount });
      toast.success("Pagamento registrado");
      setPayTarget(null); setPayAmount("");
      load(); onChanged?.();
    } catch { toast.error("Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} weight="duotone" className="text-blue-600"/>
            Subcontas de {owner?.name}
          </DialogTitle>
        </DialogHeader>

        {editing ? (
          <form onSubmit={submit} className="space-y-3 py-2" data-testid="sub-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Nome do beneficiário" required>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="sub-form-name" placeholder="Ex: João Silva"/>
              </Field>
              <Field label="Parentesco / rótulo">
                <Input value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} data-testid="sub-form-beneficiary" placeholder="Ex: Pai, Mãe, Irmão, Tia"/>
              </Field>
              <Field label="Usuário (identificador único)" required>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} disabled={editing !== "new"} required data-testid="sub-form-username" placeholder="ex: paitv"/>
              </Field>
              <Field label="Valor (R$)">
                <Input type="number" step="0.01" value={form.plan_value} onChange={(e) => setForm({ ...form, plan_value: e.target.value })} data-testid="sub-form-value"/>
              </Field>
              <Field label="Recorrência">
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger data-testid="sub-form-recurrence"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Dia do vencimento (1-28)">
                <Input type="number" min="1" max="28" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: e.target.value })} data-testid="sub-form-dueday"/>
              </Field>
              <Field label="Desconto manual (%)">
                <Input type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} data-testid="sub-form-discount"/>
              </Field>
              <Field label="Nível fidelidade (manual)">
                <Select value={form.custom_loyalty || "auto"} onValueChange={(v) => setForm({ ...form, custom_loyalty: v === "auto" ? "" : v })}>
                  <SelectTrigger data-testid="sub-form-loyalty"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático</SelectItem>
                    <SelectItem value="bronze">Bronze</SelectItem>
                    <SelectItem value="silver">Silver</SelectItem>
                    <SelectItem value="gold">Gold</SelectItem>
                    <SelectItem value="king">King</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {editing !== "new" && (
                <Field label="Próximo vencimento (opcional — sobrescreve)">
                  <Input
                    type="date"
                    value={form.current_due_date ? form.current_due_date.slice(0, 10) : ""}
                    onChange={(e) => setForm({ ...form, current_due_date: e.target.value })}
                    data-testid="sub-form-current-due"
                  />
                </Field>
              )}
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="submit" data-testid="sub-form-submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {editing === "new" ? "Criar subconta" : "Salvar alterações"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-600">
                {loading ? "Carregando..." : `${subs.length} subconta${subs.length !== 1 ? "s" : ""} vinculada${subs.length !== 1 ? "s" : ""} a este cliente.`}
              </div>
              <Button onClick={startNew} data-testid="sub-add-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus size={16} weight="bold" className="mr-1.5"/> Nova subconta
              </Button>
            </div>

            {subs.length === 0 && !loading ? (
              <Card className="border-dashed border-zinc-300 p-8 text-center text-zinc-500 text-sm">
                Este cliente ainda não tem subcontas. Use o botão acima para vincular um plano (Pai, Mãe, Irmão, etc.).
              </Card>
            ) : (
              <ul className="space-y-2" data-testid="sub-list">
                {subs.map((s) => (
                  <li key={s.id} className="border border-zinc-200 rounded-xl p-3 flex flex-wrap items-center gap-3 bg-white" data-testid={`sub-row-${s.id}`}>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {s.name}
                        {s.beneficiary && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] tracking-widest uppercase font-semibold bg-zinc-100 border border-zinc-200 text-zinc-600">
                            {s.beneficiary}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500">@{s.username} · {s.recurrence}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-widest uppercase text-zinc-500">Valor</div>
                      <div className="font-display text-sm font-semibold tab-nums">{formatBRL(s.plan_value)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-widest uppercase text-zinc-500">Venc.</div>
                      <div className="font-display text-sm font-semibold tab-nums">{formatDate(s.current_due_date)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] tracking-widest uppercase text-zinc-500">Desc.</div>
                      <div className="font-display text-sm font-semibold text-emerald-700">{s.effective_discount_pct}%</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={s.status === "ativo" ? s.payment_status : s.status} />
                      <LoyaltyBadge tier={s.loyalty_tier} size="sm" />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`sub-edit-${s.id}`}>
                        <PencilSimple size={16}/>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => { setPayTarget(s); setPayAmount(String(s.plan_value || "")); }} data-testid={`sub-pay-${s.id}`}>
                        <CurrencyDollar size={16}/>
                      </Button>
                      {s.status === "ativo" ? (
                        <Button variant="ghost" size="icon" onClick={() => setStatus(s, "inativo")} data-testid={`sub-pause-${s.id}`} title="Pausar/Inativar">
                          <Lock size={16}/>
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" onClick={() => setStatus(s, "ativo")} data-testid={`sub-resume-${s.id}`} title="Reativar">
                          <LockOpen size={16}/>
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`sub-delete-${s.id}`} className="text-rose-600">
                        <Trash size={16}/>
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!editing && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sub-close">
              <X size={14} className="mr-1.5"/> Fechar
            </Button>
          </DialogFooter>
        )}

        {/* Payment registration dialog */}
        <Dialog open={!!payTarget} onOpenChange={(v) => !v && setPayTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Registrar pagamento</DialogTitle></DialogHeader>
            {payTarget && (
              <div className="space-y-3 py-2">
                <div className="text-sm">
                  <div className="text-zinc-500 text-xs uppercase tracking-widest">Subconta</div>
                  <div className="font-medium">{payTarget.name} {payTarget.beneficiary && <span className="text-zinc-500">({payTarget.beneficiary})</span>}</div>
                </div>
                <Field label="Valor (R$)">
                  <Input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="sub-pay-amount"/>
                </Field>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPayTarget(null)}>Cancelar</Button>
                  <Button onClick={registerPay} data-testid="sub-pay-confirm" className="bg-emerald-600 hover:bg-emerald-700 text-white">Confirmar pagamento</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-[11px] tracking-widest uppercase text-zinc-500 font-semibold">{label}{required && <span className="text-rose-500"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
